/**
 * dd-cursor — follow cursor that expands with a label over product / collection cards.
 *
 * Theme-agnostic: every theme-specific value (selectors, labels, speed) arrives
 * through the JSON config rendered by sections/dd-cursor.liquid. This file should
 * not need editing when moving between themes.
 *
 * The cursor node is appended to <body> so it escapes any stacking context created
 * by whatever section the config happens to live in.
 */
(function () {
  'use strict';

  var CONFIG_ID = 'dd-cursor-config';
  var hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  var state = {
    mounted: false,
    root: null,
    inner: null,
    label: null,
    raf: null,
    targetX: 0,
    targetY: 0,
    currentX: 0,
    currentY: 0,
    seen: false,
    config: null,
  };

  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function productSelector(config) {
    return config.onProduct && config.productSelector ? config.productSelector : '';
  }

  function collectionSelector(config) {
    return config.onCollection && config.collectionSelector ? config.collectionSelector : '';
  }

  function targetSelector(config) {
    return [productSelector(config), collectionSelector(config)].filter(Boolean).join(',');
  }

  /* Controls inside a card must NOT get the expanded circle: a large disc drawn over
     a quick-add button hides the target and shows a label that contradicts the action. */
  var OPT_OUT =
    'button,[role="button"],input,select,textarea,quick-add-component,.quick-add,[data-dd-cursor="hide"]';

  function build() {
    var root = document.createElement('div');
    root.className = 'dd-cursor';
    root.setAttribute('aria-hidden', 'true');

    var inner = document.createElement('div');
    inner.className = 'dd-cursor__inner';

    var label = document.createElement('span');
    label.className = 'dd-cursor__label';

    inner.appendChild(label);
    root.appendChild(inner);
    document.body.appendChild(root);

    state.root = root;
    state.inner = inner;
    state.label = label;
  }

  function tick() {
    var ease = motionQuery.matches ? 1 : state.config.speed;
    state.currentX += (state.targetX - state.currentX) * ease;
    state.currentY += (state.targetY - state.currentY) * ease;
    state.root.style.transform =
      'translate3d(' + state.currentX + 'px,' + state.currentY + 'px,0)';
    state.raf = window.requestAnimationFrame(tick);
  }

  function startLoop() {
    if (state.raf == null) state.raf = window.requestAnimationFrame(tick);
  }

  function stopLoop() {
    if (state.raf != null) {
      window.cancelAnimationFrame(state.raf);
      state.raf = null;
    }
  }

  /* Pointer position is only stored here — all reads happen in the rAF tick, so the
     move handler never triggers layout. */
  function onPointerMove(event) {
    state.targetX = event.clientX;
    state.targetY = event.clientY;
    if (!state.seen) {
      // Snap on the first move, otherwise the dot swoops in from 0,0.
      state.currentX = event.clientX;
      state.currentY = event.clientY;
      state.seen = true;
      state.root.classList.add('is-visible');
    }
    startLoop();
  }

  function resolve(element) {
    var selector = targetSelector(state.config);
    if (!selector || !element.closest) return null;

    var card = element.closest(selector);
    if (!card) return null;

    var optOut = element.closest(OPT_OUT);
    if (optOut && card.contains(optOut)) return null;

    return card;
  }

  function labelFor(card) {
    var override = card.getAttribute('data-dd-cursor-label');
    if (override) return override;

    var collection = collectionSelector(state.config);
    if (collection && card.matches(collection)) return state.config.collectionLabel;
    return state.config.productLabel;
  }

  function onOver(event) {
    if (!(event.target instanceof Element)) return;
    var card = resolve(event.target);
    if (!card) {
      state.root.classList.remove('is-expanded');
      return;
    }
    state.label.textContent = labelFor(card);
    state.root.classList.add('is-expanded');
  }

  function onOut(event) {
    if (!(event.target instanceof Element)) return;
    var to = event.relatedTarget;
    if (to instanceof Element && resolve(to)) return;
    state.root.classList.remove('is-expanded');
  }

  function onLeaveWindow() {
    state.root.classList.remove('is-visible', 'is-expanded');
    state.seen = false;
    stopLoop();
  }

  function onVisibility() {
    if (document.hidden) stopLoop();
  }

  function mount() {
    if (state.mounted) return;
    build();
    document.body.classList.add('dd-cursor-on');
    document.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('mouseleave', onLeaveWindow);
    document.addEventListener('visibilitychange', onVisibility);
    state.mounted = true;
  }

  function unmount() {
    if (!state.mounted) return;
    stopLoop();
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('mouseover', onOver);
    document.removeEventListener('mouseout', onOut);
    document.removeEventListener('mouseleave', onLeaveWindow);
    document.removeEventListener('visibilitychange', onVisibility);
    document.body.classList.remove('dd-cursor-on');
    if (state.root && state.root.parentNode) state.root.parentNode.removeChild(state.root);
    state.root = state.inner = state.label = null;
    state.seen = false;
    state.mounted = false;
  }

  function sync() {
    var config = readConfig();
    state.config = config;

    var wanted =
      !!config && config.enabled && hoverQuery.matches && !!targetSelector(config);

    if (wanted) mount();
    else unmount();
  }

  function init() {
    sync();
    // A tablet switching between touch and a connected mouse.
    if (hoverQuery.addEventListener) hoverQuery.addEventListener('change', sync);
    // The theme editor re-renders the section on every setting change.
    document.addEventListener('shopify:section:load', function (event) {
      var target = event.target;
      if (target && target.querySelector && target.querySelector('#' + CONFIG_ID)) sync();
    });
    document.addEventListener('shopify:section:unload', sync);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
